import { useRef } from 'preact/hooks';
import type { JSX } from 'preact';

type HandlerProps = Pick<
    JSX.HTMLAttributes<HTMLElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
>;

export function useClickOrDrag(
    {
        onClick,
        onStart,
        onMove,
        onDrag,
    }: {
        onClick?: (e: PointerEvent) => void;
        onStart?: (e: PointerEvent) => void;
        onMove?: (e: PointerEvent) => void;
        onDrag?: (e: PointerEvent) => void;
    },
    threshold = 6
): HandlerProps {
    const pointerIdRef = useRef<number | null>(null);
    const activeElementRef = useRef<HTMLElement | null>(null);
    const previousTouchActionRef = useRef<string | null>(null);
    const start = useRef({ x: 0, y: 0, moved: false });

    const handleDown = (e) => {
        if (pointerIdRef.current !== null) return;
        start.current = { x: e.clientX, y: e.clientY, moved: false };
        const currentElement = e.currentTarget as HTMLElement;
        if (e.pointerType === 'touch') {
            previousTouchActionRef.current = currentElement.style.touchAction;
            currentElement.style.touchAction = 'none';
            if (e.cancelable) e.preventDefault();
        }
        currentElement.setPointerCapture(e.pointerId);
        pointerIdRef.current = e.pointerId;
        activeElementRef.current = currentElement;
        onStart?.(e);
    };

    const handleMove = (e) => {
        if (pointerIdRef.current !== e.pointerId) return;
        if (e.pointerType === 'touch' && e.cancelable) e.preventDefault();
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        if (Math.hypot(dx, dy) > threshold) start.current.moved = true;
        onMove?.(e);
    };

    const handleEnd = (e) => {
        if (pointerIdRef.current !== e.pointerId) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        if (Math.hypot(dx, dy) > threshold) start.current.moved = true;
        const currentElement = activeElementRef.current ?? (e.currentTarget as HTMLElement);
        if (currentElement.hasPointerCapture?.(e.pointerId)) {
            currentElement.releasePointerCapture(e.pointerId);
        }
        pointerIdRef.current = null;
        if (activeElementRef.current) {
            activeElementRef.current.style.touchAction = previousTouchActionRef.current ?? '';
        }
        activeElementRef.current = null;
        previousTouchActionRef.current = null;
        start.current.moved ? onDrag?.(e) : onClick?.(e);
    };

    const handleCancel = (e) => {
        if (pointerIdRef.current === e.pointerId) {
            pointerIdRef.current = null;
            if (activeElementRef.current) {
                activeElementRef.current.style.touchAction = previousTouchActionRef.current ?? '';
            }
            activeElementRef.current = null;
            previousTouchActionRef.current = null;
        }
    };

    return {
        onPointerDown: handleDown,
        onPointerMove: handleMove,
        onPointerUp: handleEnd,
        onPointerCancel: handleCancel,
    };
}
